import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Modal from '@homeaway/react-modal';
import DatePicker from '../DatePicker';
import GuestPicker from '../GuestPicker';
import { ButtonPrimary } from '@homeaway/react-buttons';
import { Checkbox } from '@homeaway/react-form-components';
import { LoadingOverlay } from '@homeaway/react-loading-overlay';
import { toURL } from '@homeaway/search-url-parser';
import Cookies from 'js-cookie';
import { removeDatesFromTripCookie, removeDatesFromSearchCookie } from '@homeaway/react-searchbox-lite/lib/transpiled/utils/cookies';
import { SEARCH_COOKIE } from '../../../config/constants';

import { triggerEdapEvent } from '../../../utils/edap';

import {
  TRENDING_MODAL_CLOSE,
  TRENDING_DATED_SEARCH,
  TRENDING_NODATED_SEARCH,
  TRENDING_NODATES_CLICK
} from './events';

import './TrendingModal.less';

const CNAME = 'TrendingModal';

class TrendingModal extends Component {
  static propTypes = {
    locale: PropTypes.string.isRequired,
    isMobile: PropTypes.bool.isRequired,
    siteName: PropTypes.string.isRequired,
    destination: PropTypes.string,
    checkIn: PropTypes.string,
    checkOut: PropTypes.string,
    lbsId: PropTypes.string,
    guests: PropTypes.shape({}),
    isOpen: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string,
    url: PropTypes.shape({
      slpUrl: PropTypes.string,
      serpUrl: PropTypes.string
    }),
    locationQuery: PropTypes.string.isRequired,
    handleClose: PropTypes.func,
    labels: PropTypes.shape({
      mobileTitle: PropTypes.string,
      modalSubtitle: PropTypes.string,
      buttonTitle: PropTypes.string,
      datePickerLabels: PropTypes.object,
      guestsLabels: PropTypes.object,
      modalDefaultErrorMsg: PropTypes.string.isRequired
    })
  }

  constructor(props) {
    super(props);

    this.state = {
      noDates: false,
      checkIn: this.props.checkIn,
      checkOut: this.props.checkOut,
      dpErrorMsg: '',
      guests: {
        adultsCount: this.props.guests.adultsCount,
        childrenCount: this.props.guests.childrenCount,
        petIncluded: this.props.guests.petIncluded
      },
      isLoadingURL: false
    };
  }
  // Modal
  handleModalClose = () => {
    triggerEdapEvent(TRENDING_MODAL_CLOSE);
    this.clearError();
  }

  handleContinue = () => {
    const { checkIn, checkOut, noDates } = this.state;
    if ((!!checkIn && !!checkOut) || noDates) {
      triggerEdapEvent(noDates ? TRENDING_NODATED_SEARCH : TRENDING_DATED_SEARCH);
      this.setState({ isLoadingURL: true });
      this.navigateTo(this.getURL());
    } else {
      this.showError();
    }
  }

  getURL = () => {
    let searchUrl;
    const { checkIn, checkOut, noDates, guests: { adultsCount, childrenCount, petIncluded }} = this.state;
    const { siteName, url: { slpUrl }, locationQuery } = this.props;

    if (noDates) {
      removeDatesFromSearchCookie();
      removeDatesFromTripCookie();
    }

    if (noDates && slpUrl) {
      // noDates and SLP url -> directly to SLP
      const myCookie = { ...Cookies.getJSON(SEARCH_COOKIE), numberOfAdults: adultsCount, numberOfChildren: childrenCount, petIncluded };
      Cookies.set(SEARCH_COOKIE, myCookie);
      searchUrl = slpUrl;
    } else {
      // Dates or no SLP url -> to SERP with params
      const searchRequest = {
        q: locationQuery,
        arrival: noDates ? '' : checkIn,
        departure: noDates ? '' : checkOut,
        adultsCount: adultsCount || 0,
        childrenCount: childrenCount || 0,
        petIncluded: !!petIncluded
      };
      searchUrl = toURL(searchRequest, { name: siteName });
    }
    return searchUrl;
  }

  navigateTo = (url) => {
    window.location = url;
  }

  showError = () => {
    const { modalDefaultErrorMsg } = this.props.labels;
    this.setState({ dpErrorMsg: modalDefaultErrorMsg });
  }

  clearError = () => {
    this.setState({ dpErrorMsg: '' });
  }

  // DatePicker
  onDateRangeChange = (checkinDate, checkoutDate) => {
    if (!!checkinDate && !!checkoutDate) {
      this.setState({ dpErrorMsg: '', noDates: false });
    }
    this.setState({ checkIn: checkinDate, checkOut: checkoutDate });
  }

  onDateRangeClose = () => {
    if (this.state.checkOut === null) {
      this.setState({ checkIn: '', checkOut: '' });
    }
  }

  // GuestPicker
  onGuestPickerSelectorChange = (key, value) => {
    this.setState({ guests: { ...this.state.guests, [key]: value }});
  }

  onGuestPickerSelectorClose = () => {
    if (this.props.isMobile) {
      this.handleContinue();
    }
  }

  // Checkbox
  onCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    this.setState({ [event.target.name]: isChecked });
    if (isChecked && this.state.dpErrorMsg) {
      this.clearError();
    }
    triggerEdapEvent(TRENDING_NODATES_CLICK, { actionstate: isChecked ? 'on' : 'off' });
  }

  render() {
    const { checkIn, checkOut, guests, noDates, dpErrorMsg, isLoadingURL } = this.state;
    const { locale, locationQuery, isOpen, handleClose, isMobile } = this.props;
    const { modalTitle, modalSubtitle, mobileTitle, buttonTitle, datePickerLabels, guestsLabels, checkboxLabel } = this.props.labels;

    return (
      <Modal
        id="trending-modal"
        title={modalTitle}
        locationQuery = {locationQuery}
        mobileTitle = {mobileTitle}
        buttonTitle = {buttonTitle}
        isOpen={isOpen}
        onModalOpen = {this.handleModalOpen}
        onModalClose = {this.handleModalClose}
        onClose={handleClose}
        footer={
          isLoadingURL ?
            <ButtonPrimary label={buttonTitle} disabled>
              <LoadingOverlay />
            </ButtonPrimary>
            :
            <ButtonPrimary onClick={this.handleContinue} label={buttonTitle} />
        }
        className= {CNAME}
        isWorkflow
      >
        <p className={`${CNAME}__subtitle`}>{modalSubtitle}</p>
        <form>
          <div className={`${CNAME}__content`}>
            {(isMobile && dpErrorMsg) && <div className="has-error"><p className="help-block">{dpErrorMsg}</p></div>}
            <DatePicker
              onDateRangeChange = {this.onDateRangeChange}
              onDateRangeClose = {this.onDateRangeClose}
              checkIn={checkIn}
              checkOut={checkOut}
              labels={datePickerLabels}
              isMobile={isMobile}
              dpErrorMsg={dpErrorMsg}
              locale={locale}
              noDates = {noDates}
            />

            <GuestPicker
              {...{ guests }}
              onSelectorChange={this.onGuestPickerSelectorChange}
              onGuestPickerClose = {this.onGuestPickerSelectorClose}
              labels={guestsLabels}
            />

            <Checkbox
              name="noDates"
              label={checkboxLabel}
              error
              size="sm"
              checked={noDates}
              onChange={this.onCheckboxChange}
            />
          </div>
        </form>
      </Modal>

    );
  }
}

export default TrendingModal;



// WEBPACK FOOTER //
// ./src/universal/components/Trending/TrendingModal/TrendingModal.js